// noinspection DuplicatedCode

import { ClinicId } from '@app/icare/entities/clinic/schema/Clinic'
import { HospiceId } from '@app/icare/entities/hospice/schema/Hospice'
import { NursingHomeId } from '@app/icare/entities/nursingHome/schema/NursingHome'
import { msg } from '@lingui/core/macro'
import { entityId } from '@pkg/db/schema'
import { defineEnum } from '@pkg/shared/utils/enum-builder'
import { FaHospital, FaHouseMedicalFlag, FaHouseUser, FaPersonWalkingLuggage } from 'react-icons/fa6'
import { z } from 'zod'
import { PatientRelation } from './PatientRelation'

export const PatientSeparateResidenceEntityDiscriminatorEnum = defineEnum({
  clinic: {
    label: msg`Rehaklinik`,
    icon: FaHospital,
    description: msg`Aufenthalt in einer Rehaklinik`,
  },
  hospice: {
    label: msg`Hospiz`,
    icon: FaHouseMedicalFlag,
    description: msg`Hospizaufenthalt`,
  },
  nursingHome: {
    label: msg`Pflegeheim`,
    icon: FaHouseUser,
    description: msg`Pflegeheimaufenthalt`,
  },
})

export type PatientSeparateResidenceEntityDiscriminatorType =
  typeof PatientSeparateResidenceEntityDiscriminatorEnum.$.Type

export const PatientSeparateResidenceDiscriminatorEnum = PatientSeparateResidenceEntityDiscriminatorEnum.$.extend({
  patientAddress: {
    label: msg`Urlaubsort`,
    icon: FaPersonWalkingLuggage,
    description: msg`Vorübergehender Aufenthaltsort des Patienten`,
  },
})

export type PatientSeparateResidenceDiscriminatorEnum = typeof PatientSeparateResidenceDiscriminatorEnum.$.Type

export const PatientSeparateResidenceId = entityId('patientSeparateResidence')

export type PatientSeparateResidenceIdType = typeof PatientSeparateResidenceId.Type

export const PatientSeparateResidenceAddressTypeEnum = defineEnum({
  default: msg`Standard`,
  other: msg`Andere`,
})

export type PatientSeparateResidenceAddressType = typeof PatientSeparateResidenceAddressTypeEnum.$.Type

const SeparateResidenceAlternativeAddress = z.object({
  type: z
    .literal(PatientSeparateResidenceAddressTypeEnum.other)
    .nullish()
    .default(PatientSeparateResidenceAddressTypeEnum.other),
  firstName: z.string().nullish(),
  lastName: z.coerce.string().nullish(),
  addition: z.string().nullish(),
  comment: z.string().nullish(),
  address: z
    .object({
      streetAddress: z.coerce.string().nullish(),
      zipCode: z.coerce.string().nullish(),
      city: z.coerce.string().nullish(),
    })
    .nullish(),
})

export const SeparateResidenceDefaultAddress = z.object({
  type: z
    .literal(PatientSeparateResidenceAddressTypeEnum.default)
    .default(PatientSeparateResidenceAddressTypeEnum.default),
  addition: z.string().nullish(),
})

export const SeparateResidenceAddress = z.discriminatedUnion('type', [
  SeparateResidenceAlternativeAddress,
  SeparateResidenceDefaultAddress,
])

const PatientSeparateResidenceDefaultSchema = PatientRelation.extend({
  id: PatientSeparateResidenceId.schema,
  type: z.enum(PatientSeparateResidenceDiscriminatorEnum.$.zodEnum()),
  delivery: SeparateResidenceAddress,
  billing: SeparateResidenceAddress,
  comment: z.string().nullish(),
})

export const PatientSeparateResidenceAddressRelationSchema = PatientSeparateResidenceDefaultSchema.extend({
  type: z
    .literal(PatientSeparateResidenceDiscriminatorEnum.patientAddress)
    .default(PatientSeparateResidenceDiscriminatorEnum.patientAddress),
  toId: z.union([z.undefined(), z.null()]).nullish(),
  firstName: z.string().nullish(),
  lastName: z.coerce.string().nullish(),
  address: z
    .object({
      streetAddress: z.coerce.string().nullish(),
      zipCode: z.coerce.string().nullish(),
      city: z.coerce.string().nullish(),
    })
    .default({}),
})

export type PatientSeparateResidenceAddressRelation = z.infer<typeof PatientSeparateResidenceAddressRelationSchema>

export const PatientSeparateResidenceRelationSchema = z.discriminatedUnion('type', [
  PatientSeparateResidenceAddressRelationSchema,
  PatientSeparateResidenceDefaultSchema.extend({
    type: z
      .literal(PatientSeparateResidenceDiscriminatorEnum.hospice)
      .default(PatientSeparateResidenceDiscriminatorEnum.hospice),
    toId: HospiceId.ref,
    firstName: z.never().nullish(),
    lastName: z.never().nullish(),
    address: z.never().nullish(),
  }),
  PatientSeparateResidenceDefaultSchema.extend({
    type: z
      .literal(PatientSeparateResidenceDiscriminatorEnum.nursingHome)
      .default(PatientSeparateResidenceDiscriminatorEnum.nursingHome),
    toId: NursingHomeId.ref,
    firstName: z.never().nullish(),
    lastName: z.never().nullish(),
    address: z.never().nullish(),
  }),
  PatientSeparateResidenceDefaultSchema.extend({
    type: z
      .literal(PatientSeparateResidenceDiscriminatorEnum.clinic)
      .default(PatientSeparateResidenceDiscriminatorEnum.clinic),
    toId: ClinicId.ref,
    firstName: z.never().nullish(),
    lastName: z.never().nullish(),
    address: z.never().nullish(),
  }),
])

export type PatientSeparateResidenceRelation = z.infer<typeof PatientSeparateResidenceRelationSchema>
